import { RegionalScope, RegionId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptionsRpc } from "util/requests";

export type ActivityTypeRow = inferProcedureOutput<
    AppRouter["taskType"]["taskTypes"]
>[number];

export interface ActivityTypeRowsState {
    rows: ActivityTypeRow[] | undefined;
    isLoading: boolean;
}

export interface ScopedActivityTypeRowsState {
    rows: ActivityTypeRow[] | undefined;
    error: unknown;
}

export const useActivityTypeRows = (): ActivityTypeRowsState => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.taskType.taskTypes.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        ...stableQueryOptionsRpc(),
        enabled: !!regionId,
    });
    return { rows: query.data, isLoading: query.isLoading };
};

export const useScopedActivityTypeRows = (
    scope: RegionalScope,
): ScopedActivityTypeRowsState => {
    const trpcClient = trpc.useTRPC();
    const agencyQuery = useQuery({
        ...trpcClient.taskType.agencyTaskTypes.queryOptions(),
        ...stableQueryOptionsRpc(),
        enabled: scope.kind === "agencyWide",
    });
    const officeQuery = useQuery({
        ...trpcClient.taskType.taskTypes.queryOptions({
            regionId: scope.kind === "office" ? scope.regionId : ("" as RegionId),
        }),
        ...stableQueryOptionsRpc(),
        enabled: scope.kind === "office",
    });
    if (scope.kind === "office") {
        return { rows: officeQuery.data, error: officeQuery.error };
    }
    return { rows: agencyQuery.data, error: agencyQuery.error };
};
